import type { ClassificationContent } from '@classification-testing/domain';
import { ContentType, ClassificationInputTab } from '@classification-testing/domain';
import type { DomainError } from '@core/domain';
import {
  AudioContentRenderer,
  ImageContentRenderer,
  TextInputCard,
  AudioRecordingCard,
  ImageUrlInputCard,
  UploadTabContent,
} from '@soteria-ui/src/classification-testing/components';

interface TabContentProps {
  activeTab: ClassificationInputTab;
  content: ClassificationContent | null;
  pendingFile: File | null;
  errors: DomainError[] | null;
  isUploading: boolean;
  isRecording: boolean;
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onImageUrlChange: (newImageUrl: string) => void;
  onClear: () => void;
  onFileSelect: (file: File) => void;
  onFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste: (event: React.ClipboardEvent) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
}

interface TextTabProps {
  hasError: boolean;
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  content: ClassificationContent | null;
  onClear: () => void;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

interface RecordTabProps {
  content: ClassificationContent | null;
  hasError: boolean;
  isRecording: boolean;
  onClear: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
}

interface ImageUrlTabProps {
  content: ClassificationContent | null;
  hasError: boolean;
  onClear: () => void;
  onImageUrlChange: (newImageUrl: string) => void;
}

function renderTextTab(props: TextTabProps) {
  return (
    <TextInputCard
      hasError={props.hasError}
      textAreaRef={props.textAreaRef}
      textContent={props.content?.text || ''}
      onClear={props.onClear}
      onTextChange={props.onTextChange}
    />
  );
}

function renderRecordTab(props: RecordTabProps) {
  const hasRecordedAudio = props.content?.type === ContentType.AUDIO && props.content.audioUrl;

  if (hasRecordedAudio && props.content) {
    return <AudioContentRenderer audioUrl={props.content.audioUrl || ''} hasError={props.hasError} onClear={props.onClear} />;
  }

  return <AudioRecordingCard isActive isRecording={props.isRecording} onStartRecording={props.onStartRecording} onStopRecording={props.onStopRecording} />;
}

function renderImageUrlTab(props: ImageUrlTabProps) {
  const hasImageUrl = props.content?.type === ContentType.IMAGE && props.content.imageUrl;

  if (hasImageUrl && props.content) {
    return <ImageContentRenderer hasError={props.hasError} imageUrl={props.content.imageUrl || ''} isLoading={false} onClear={props.onClear} />;
  }

  return <ImageUrlInputCard hasError={props.hasError} imageUrl={props.content?.imageUrl || ''} onClear={props.onClear} onUrlChange={props.onImageUrlChange} />;
}

/**
 * TabContent component to render content based on active tab
 * Extracts complex conditional rendering logic from the main component
 */
export function TabContent({
  activeTab,
  content,
  pendingFile,
  errors,
  isUploading,
  isRecording,
  textAreaRef,
  fileInputRef,
  onTextChange,
  onImageUrlChange,
  onClear,
  onFileSelect,
  onFileInputChange,
  onPaste,
  onStartRecording,
  onStopRecording,
}: TabContentProps) {
  const hasError = !!(errors && errors.length > 0);

  if (activeTab === ClassificationInputTab.TEXT) {
    return renderTextTab({ hasError, textAreaRef, content, onClear, onTextChange });
  }

  if (activeTab === ClassificationInputTab.UPLOAD) {
    return (
      <UploadTabContent
        content={content}
        errors={errors}
        fileInputRef={fileInputRef}
        isUploading={isUploading}
        pendingFile={pendingFile}
        onClear={onClear}
        onFileInputChange={onFileInputChange}
        onFileSelect={onFileSelect}
        onPaste={onPaste}
      />
    );
  }

  if (activeTab === ClassificationInputTab.RECORD) {
    return renderRecordTab({ content, hasError, isRecording, onClear, onStartRecording, onStopRecording });
  }

  if (activeTab === ClassificationInputTab.IMAGE_URL) {
    return renderImageUrlTab({ content, hasError, onClear, onImageUrlChange });
  }

  return null;
}
